import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";
import PublicLayout from "@/components/site/PublicLayout";

import Home from "@/pages/Home";
import SlugRouter from "@/pages/SlugRouter";
import PartnerWithUs from "@/pages/PartnerWithUs";
import RevenueCalculator from "@/pages/RevenueCalculator";
import StaffingCalculator from "@/pages/StaffingCalculator";
import StaffingCMS from "@/pages/admin/StaffingCMS";
import Analytics from "@/pages/admin/Analytics";
import RevenueCalcCMS from "@/pages/admin/RevenueCalcCMS";
import PartnershipCMS from "@/pages/admin/PartnershipCMS";
import PartnershipModels from "@/pages/PartnershipModels";
import PropertyReadiness from "@/pages/PropertyReadiness";
import ReadinessChecklist from "@/pages/admin/ReadinessChecklist";
import AssessmentEditor, { AssessmentsList } from "@/pages/admin/ReadinessAssessments";
import { BlogList, BlogDetail } from "@/pages/Blog";

import AdminLogin from "@/pages/admin/Login";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import { PropertiesList, PropertyEdit } from "@/pages/admin/Properties";
import PageCMS from "@/pages/admin/PageCMS";
import { BlogList as AdminBlogList, BlogEdit } from "@/pages/admin/Blog";
import { SEOPagesList, SEOPageEdit } from "@/pages/admin/SEOPages";
import { PagesList, PageEdit } from "@/pages/admin/Pages";
import { Testimonials, FAQs, Gallery } from "@/pages/admin/Simple";
import Leads from "@/pages/admin/Leads";
import Settings from "@/pages/admin/Settings";
import Tracking from "@/pages/admin/Tracking";
import Users from "@/pages/admin/Users";
import MediaLibrary from "@/pages/admin/Media";
import AdminMenu from "@/pages/admin/Menu";
import Health from "@/pages/admin/Health";
import Activity from "@/pages/admin/Activity";
import NotFound from "@/pages/NotFound";

function PublicShell({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <Toaster richColors position="top-center" />
        <BrowserRouter>
          <Routes>
            {/* Admin (no layout chrome) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="pages" element={<PagesList />} />
              <Route path="pages/home" element={<PageCMS pageId="home" />} />
              <Route path="pages/partner" element={<PageCMS pageId="partner" />} />
              <Route path="pages/revenue-calculator" element={<RevenueCalcCMS />} />
              <Route path="pages/staffing-calculator" element={<StaffingCMS />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="pages/partnership-models" element={<PartnershipCMS />} />
              <Route path="property-readiness" element={<AssessmentsList />} />
              <Route path="property-readiness/assessments" element={<AssessmentsList />} />
              <Route path="property-readiness/assessments/:id" element={<AssessmentEditor />} />
              <Route path="property-readiness/master-checklist" element={<ReadinessChecklist />} />
              <Route path="pages/:id" element={<PageEdit />} />
              <Route path="properties" element={<PropertiesList />} />
              <Route path="properties/:id" element={<PropertyEdit />} />
              <Route path="seo-pages" element={<SEOPagesList />} />
              <Route path="seo-pages/:id" element={<SEOPageEdit />} />
              <Route path="blog" element={<AdminBlogList />} />
              <Route path="blog/:id" element={<BlogEdit />} />
              <Route path="leads" element={<Leads />} />
              <Route path="testimonials" element={<Testimonials />} />
              <Route path="faqs" element={<FAQs />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="media" element={<MediaLibrary />} />
              <Route path="menu" element={<AdminMenu />} />
              <Route path="health" element={<Health />} />
              <Route path="activity" element={<Activity />} />
              <Route path="settings" element={<Settings />} />
              <Route path="tracking" element={<Tracking />} />
              <Route path="users" element={<Users />} />
            </Route>

            {/* Public site */}
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/blog" element={<PublicShell><BlogList /></PublicShell>} />
            <Route path="/blog/:slug" element={<PublicShell><BlogDetail /></PublicShell>} />
            <Route path="/partner-with-us" element={<PublicShell><PartnerWithUs /></PublicShell>} />
            <Route path="/revenue-calculator" element={<PublicShell><RevenueCalculator /></PublicShell>} />
            <Route path="/staffing-calculator" element={<PublicShell><StaffingCalculator /></PublicShell>} />
            <Route path="/partnership-models" element={<PublicShell><PartnershipModels /></PublicShell>} />
            <Route path="/property-readiness" element={<PublicShell><PropertyReadiness /></PublicShell>} />
            <Route path="/property-readiness/:token" element={<PublicShell><PropertyReadiness /></PublicShell>} />

            {/* Dynamic slug — resolves to property or SEO landing */}
            <Route path="/:slug" element={<PublicShell><SlugRouter /></PublicShell>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
